import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Zap, Check, Menu, X, Rocket, Globe,
  Gauge, Layers, Wrench, Mail, Palette, Cog, TestTube, PartyPopper,
  AppWindow, Gamepad2, Sparkles, ArrowRight
} from 'lucide-react';
import { motion } from 'framer-motion';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const focusAreas = [
    {
      icon: <AppWindow className="w-7 h-7" />,
      title: 'Applications',
      desc: 'Everyday applications designed to enhance productivity and streamline digital workflows with intuitive interfaces and reliable performance.',
    },
    {
      icon: <Wrench className="w-7 h-7" />,
      title: 'Utilities',
      desc: 'Focused utility tools that optimize system performance, automate repetitive tasks, and simplify technical processes.',
    },
    {
      icon: <Gamepad2 className="w-7 h-7" />,
      title: 'Games',
      desc: 'Lightweight, engaging games built for quick sessions and casual play across desktop and mobile.',
    },
  ];

  const values = [
    { icon: <Rocket className="w-5 h-5" />, title: 'Ship Deliberately', desc: 'We build a small number of things well rather than everything at once.', accent: 'teal' },
    { icon: <Shield className="w-5 h-5" />, title: 'Privacy by Default', desc: 'User data protection is a starting constraint, not an afterthought.', accent: 'ember' },
    { icon: <Gauge className="w-5 h-5" />, title: 'Performance Matters', desc: 'Every release is measured for speed, stability, and resource use.', accent: 'teal' },
    { icon: <Globe className="w-5 h-5" />, title: 'Built to Last', desc: 'We maintain what we ship and support it well past launch day.', accent: 'ember' },
  ];

  const techGroups = [
    { icon: <AppWindow className="w-5 h-5" />, title: 'App Development', items: ['React', 'TypeScript', 'Swift', 'Kotlin'] },
    { icon: <Layers className="w-5 h-5" />, title: 'Backend & Cloud', items: ['Node.js', 'Python', 'AWS', 'Docker'] },
    { icon: <Gauge className="w-5 h-5" />, title: 'Tooling & Data', items: ['SQLite', 'Redis', 'GraphQL', 'CI/CD'] },
  ];

  const process = [
    { icon: <Palette className="w-6 h-6" />, title: 'Design', desc: 'Sketch the smallest version of the idea that solves the real problem.' },
    { icon: <Cog className="w-6 h-6" />, title: 'Build', desc: 'Develop with a focus on simplicity, performance, and maintainability.' },
    { icon: <TestTube className="w-6 h-6" />, title: 'Test', desc: 'Validate reliability and edge cases before anything ships.' },
    { icon: <PartyPopper className="w-6 h-6" />, title: 'Support', desc: 'Keep listening and iterating well after release.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/80 backdrop-blur-xl shadow-lg shadow-teal-900/5 border-b border-cloud-200/70' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/30">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
              </div>
              <span className="text-2xl font-bold font-display text-cloud-900">
                AppsLab
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-cloud-600 hover:text-teal-700 transition-colors font-medium">About</a>
              <a href="#focus" className="text-cloud-600 hover:text-teal-700 transition-colors font-medium">What We Build</a>
              <a href="#technology" className="text-cloud-600 hover:text-teal-700 transition-colors font-medium">Technology</a>
              <Link to="/privacy" className="text-cloud-600 hover:text-teal-700 transition-colors font-medium">Privacy</Link>
              <Link to="/terms" className="text-cloud-600 hover:text-teal-700 transition-colors font-medium">Terms</Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              className="md:hidden text-cloud-700 p-2 -mr-2 cursor-pointer"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-cloud-200">
            <div className="px-4 py-4 space-y-4">
              <a href="#about" onClick={() => setIsMenuOpen(false)} className="block text-cloud-600 hover:text-teal-700">About</a>
              <a href="#focus" onClick={() => setIsMenuOpen(false)} className="block text-cloud-600 hover:text-teal-700">What We Build</a>
              <a href="#technology" onClick={() => setIsMenuOpen(false)} className="block text-cloud-600 hover:text-teal-700">Technology</a>
              <Link to="/privacy" onClick={() => setIsMenuOpen(false)} className="block text-cloud-600 hover:text-teal-700">Privacy</Link>
              <Link to="/terms" onClick={() => setIsMenuOpen(false)} className="block text-cloud-600 hover:text-teal-700">Terms</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - animates on mount, always visible */}
      <section id="about" className="relative pt-32 pb-24 px-4 overflow-hidden">
        {/* Decorative background - scoped to hero only */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-teal-200/30 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-ember-200/30 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute inset-0 opacity-[0.035]" style={{
            backgroundImage: `linear-gradient(rgba(13, 148, 136, 0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(13, 148, 136, 0.6) 1px, transparent 1px)`,
            backgroundSize: '56px 56px'
          }}></div>
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15, duration: 0.4, ease: 'easeOut' }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-full text-teal-700 font-medium mb-6"
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-sm">A Software Company</span>
            </motion.div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold font-display mb-6 leading-[1.05] tracking-tight">
              <span className="text-cloud-900">We Build </span>
              <span className="bg-gradient-to-r from-teal-600 via-teal-500 to-ember-500 bg-clip-text text-transparent">
                Focused Software
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-cloud-600 mb-4 leading-relaxed max-w-2xl mx-auto">
              <span className="font-semibold text-cloud-900">AppsLab LLC</span> designs and develops applications,
              utilities, and games that aim to do a handful of things well - simple, dependable software without
              the bloat.
            </p>
            <p className="text-cloud-500 leading-relaxed max-w-2xl mx-auto mb-10">
              This site covers who we are and how we operate. For details on how we handle data, see our{' '}
              <Link to="/privacy" className="text-teal-700 font-medium hover:text-teal-600">Privacy Policy</Link>{' '}
              and{' '}
              <Link to="/terms" className="text-teal-700 font-medium hover:text-teal-600">Terms of Service</Link>.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3">
              {['Applications', 'Utilities', 'Games'].map((tag) => (
                <span key={tag} className="px-4 py-2 bg-white border border-cloud-200 rounded-full text-sm font-semibold text-cloud-700 shadow-sm">
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Focus Areas */}
      <section id="focus" className="relative py-20 px-4 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-ember-50 border border-ember-200 rounded-full text-ember-700 font-medium mb-6">
              <Layers className="w-4 h-4" />
              <span className="text-sm">What We Build</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold font-display mb-6 tracking-tight">
              <span className="text-cloud-900">Three Areas of </span>
              <span className="bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent">
                Focus
              </span>
            </h2>
            <p className="text-lg text-cloud-600 max-w-2xl mx-auto">
              Everything we ship falls into one of these categories.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {focusAreas.map((area, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -6 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className={`group relative p-8 rounded-3xl overflow-hidden shadow-sm ${
                  i === 0
                    ? 'bg-gradient-to-br from-teal-600 to-teal-800 text-white shadow-lg shadow-teal-900/20'
                    : 'bg-white border border-cloud-200 hover:border-teal-300 hover:shadow-xl hover:shadow-teal-900/5 transition-shadow'
                }`}
              >
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform ${
                  i === 0 ? 'bg-white/15' : 'bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg shadow-teal-500/20'
                }`}>
                  {area.icon}
                </div>
                <h3 className={`text-xl font-bold font-display mb-3 ${i === 0 ? 'text-white' : 'text-cloud-900'}`}>{area.title}</h3>
                <p className={i === 0 ? 'text-teal-100 leading-relaxed' : 'text-cloud-500 leading-relaxed'}>{area.desc}</p>
                {i === 0 && <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-white/10 rounded-full blur-2xl"></div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-50 via-white to-ember-50"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-14">
            <h2 className="text-4xl sm:text-5xl font-bold font-display text-cloud-900 mb-4 tracking-tight">
              How We Work
            </h2>
            <p className="text-lg text-cloud-600">
              The principles that guide every product decision
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -6 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="group relative p-6 bg-white rounded-3xl border border-cloud-200 hover:border-teal-300 hover:shadow-xl hover:shadow-teal-900/5 transition-shadow shadow-sm"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-white shadow-md group-hover:scale-110 transition-transform ${
                  v.accent === 'teal' ? 'bg-gradient-to-br from-teal-500 to-teal-600 shadow-teal-500/25' : 'bg-gradient-to-br from-ember-500 to-ember-600 shadow-ember-500/25'
                }`}>
                  {v.icon}
                </div>
                <h4 className="font-bold text-cloud-900 mb-2">{v.title}</h4>
                <p className="text-cloud-500 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology */}
      <section id="technology" className="relative py-24 px-4 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-full text-teal-700 font-medium mb-6">
              <Zap className="w-4 h-4" />
              <span className="text-sm">Under the Hood</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold font-display mb-6 tracking-tight">
              <span className="text-cloud-900">Technology We </span>
              <span className="bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent">
                Rely On
              </span>
            </h2>
            <p className="text-lg text-cloud-600 max-w-2xl mx-auto">
              A pragmatic, modern stack chosen for reliability over novelty.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {techGroups.map((group, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="p-6 bg-cloud-50 rounded-2xl border border-cloud-200 hover:border-teal-300 hover:shadow-lg hover:shadow-teal-900/5 transition-shadow"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-cloud-200 text-teal-600">
                    {group.icon}
                  </div>
                  <h3 className="font-bold text-cloud-900">{group.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.items.map((item, j) => (
                    <span key={j} className="px-3 py-1.5 bg-white border border-cloud-200 rounded-lg text-sm text-cloud-700 font-medium">
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cloud-900 via-teal-950 to-cloud-900"></div>
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.5) 1px, transparent 1px)', backgroundSize: '36px 36px' }}></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full text-teal-200 font-medium mb-6">
              <ArrowRight className="w-4 h-4" />
              <span className="text-sm">Idea to Release</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold font-display mb-6 tracking-tight text-white">
              Our <span className="bg-gradient-to-r from-teal-300 to-ember-300 bg-clip-text text-transparent">Process</span>
            </h2>
            <p className="text-lg text-teal-100/80 max-w-2xl mx-auto">
              A simple, repeatable loop from idea to a supported release.
            </p>
          </div>

          <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0">
            {/* Connecting line - desktop only */}
            <div className="hidden lg:block absolute top-9 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-teal-500/0 via-white/25 to-ember-500/0"></div>

            {process.map((step, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -6 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="relative flex flex-col items-center text-center lg:px-4"
              >
                <div className="relative z-10 w-[72px] h-[72px] rounded-2xl bg-gradient-to-br from-teal-500 to-ember-500 flex items-center justify-center text-white shadow-lg shadow-black/30 mb-5 ring-4 ring-teal-950">
                  {step.icon}
                </div>
                <div className="text-xs font-bold text-ember-300 mb-2 tracking-wide">STEP {i + 1}</div>
                <h4 className="font-bold text-white mb-2 text-lg font-display">{step.title}</h4>
                <p className="text-teal-100/70 text-sm leading-relaxed max-w-[220px]">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-ember-50"></div>

        <div className="max-w-3xl mx-auto relative z-10 text-center">
          <div className="p-10 sm:p-14 bg-white rounded-[2rem] border border-cloud-200 shadow-xl shadow-teal-900/5">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl text-white shadow-lg shadow-teal-500/25">
                <Mail className="w-7 h-7" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold font-display mb-4 text-cloud-900 tracking-tight">
              Get in Touch
            </h2>
            <p className="text-lg text-cloud-600 mb-6 max-w-xl mx-auto leading-relaxed">
              Questions about AppsLab LLC, our products, or your data? We'd like to hear from you.
            </p>
            <a
              href="mailto:contact@appslabllc.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-500 rounded-xl text-white font-semibold shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 transition-shadow"
            >
              <Mail className="w-4 h-4" />
              contact@appslabllc.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-cloud-200 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-700 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold font-display text-cloud-900">AppsLab</span>
              <span className="text-cloud-400 text-sm">LLC</span>
            </div>
            <div className="flex gap-6 text-sm text-cloud-500">
              <Link to="/terms" className="hover:text-teal-700 transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-teal-700 transition-colors">Privacy Policy</Link>
              <a href="mailto:contact@appslabllc.com" className="hover:text-teal-700 transition-colors">Contact</a>
            </div>
          </div>
          <div className="text-center text-cloud-400 text-xs mt-6">
            &copy; 2026 AppsLab LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
